/**
 * SOLID Review
 * S — Strong. One concern: the dice ranking as data — Meyer's combinations as the server ranks them, and the
 *     command that keeps them in the session.
 * O — Good. A new field of a rank is one key here, once the server's `DiceRank` carries it.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. A rank, the ranking, and the one command.
 * D — Good. zod and the shared primitives only.
 */
import { z } from 'zod';
import { MeyerRank, DieFace } from './primitives';

/**
 * THE RULES THE TABLE IS PLAYED BY, AS DATA (frontend-domain-oo P2, 2026-10-26). The owner's decision of
 * 2026-10-11: the dice ranking lives ONLY in the backend (`DicePair.catalogue()`), and the client is handed it —
 * the `diceRanking` query (§28.268). Until P2 the client kept a copy of its own (`DiceRankingService.ts`: the
 * scores and two decoders) and a third rule for doubles, in `game/RevealTransforms.ts`, that disagreed with both
 * about Little Meyer.
 *
 * A rank is what the server says about one combination. The client keeps it as sent and asks it through the
 * `DiceRanking` value object (`dice/DiceRanking.ts`). Types, not ranges, as for every client schema: a die is an
 * integer and so is a place in the order; which values occur is the server's to say.
 */
const ClientDiceRank = z.object({
  /** The combination's score, the number the order is kept in: 32…65, a pair 110…160, Little Meyer 900, Meyer 1000. */
  score: MeyerRank,
  /** The dice, high first. */
  die1: DieFace,
  die2: DieFace,
  /** How the client names it: "5-4", "Pair 1s", "Little Meyer (31)", "Meyer (21)". */
  label: z.string(),
  isMeyer: z.boolean(),
  /** A pair, 11 to 66 — Little Meyer is not one. */
  isDouble: z.boolean(),
  /** Its place in the order, 1 the lowest. */
  rank: z.number().int(),
}).meta({ id: 'ClientDiceRank' });

/** Every combination, lowest first, as `diceRanking` serves it; empty until it has arrived. */
export const ClientDiceRanking = z.array(ClientDiceRank).meta({ id: 'ClientDiceRanking' });

/** The server's ranking, read: kept in the session, unless it is the one held or holds nothing. */
export const CommandDiceRankingLoaded = z.object({ ranking: ClientDiceRanking }).meta({ id: 'CommandDiceRankingLoaded' });

export type IEntityClientDiceRank = z.infer<typeof ClientDiceRank>;
export type ICommandDiceRankingLoaded = z.infer<typeof CommandDiceRankingLoaded>;
